import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { storage } from '../../data/localStorage';

const { FiBell, FiUser, FiLogOut, FiChevronDown, FiSearch, FiMoon, FiSun, FiCalendar, FiClock, FiX, FiChevronRight } = FiIcons;

const Header = ({ onProfileClick }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    loadNotifications();
    
    // Check for upcoming exams daily
    const checkInterval = setInterval(loadNotifications, 3600000); // Check every hour
    
    return () => clearInterval(checkInterval);
  }, []);
  
  const loadNotifications = () => {
    try {
      const exams = storage.getExams();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const dayAfterTomorrow = new Date(today);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
      
      // Get exams happening today or tomorrow
      const upcomingExams = exams.filter(exam => {
        const examDate = new Date(exam.date);
        examDate.setHours(0, 0, 0, 0);
        
        const isToday = examDate.getTime() === today.getTime();
        const isTomorrow = examDate.getTime() === tomorrow.getTime();
        
        return (isToday || isTomorrow) && exam.status !== 'completed' && exam.status !== 'cancelled';
      });
      
      // Create notifications
      const newNotifications = upcomingExams.map(exam => {
        const examDate = new Date(exam.date);
        examDate.setHours(0, 0, 0, 0);
        const isToday = examDate.getTime() === today.getTime();
        
        return {
          id: `exam-${exam.id}`,
          title: isToday ? "Exam Today" : "Exam Tomorrow",
          message: `${exam.title} (${exam.courseCode}) is scheduled ${isToday ? 'today' : 'tomorrow'} at ${exam.time}`,
          type: isToday ? "urgent" : "reminder",
          examId: exam.id,
          createdAt: new Date().toISOString(),
          read: false
        };
      });
      
      // Store and update notifications
      const storedNotifications = JSON.parse(localStorage.getItem('notifications') || '[]');
      
      // Filter out duplicates by examId
      const existingExamIds = storedNotifications.map(n => n.examId);
      const filteredNew = newNotifications.filter(n => !existingExamIds.includes(n.examId));
      
      const allNotifications = [...storedNotifications, ...filteredNew]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 50); // Keep only the latest 50
      
      localStorage.setItem('notifications', JSON.stringify(allNotifications));
      setNotifications(allNotifications);
      setNotificationCount(allNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };
  
  const markAsRead = (notificationId) => {
    const updatedNotifications = notifications.map(notification => 
      notification.id === notificationId ? { ...notification, read: true } : notification
    );
    
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
    setNotifications(updatedNotifications);
    setNotificationCount(updatedNotifications.filter(n => !n.read).length);
  };
  
  const markAllAsRead = () => {
    const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
    setNotifications(updatedNotifications);
    setNotificationCount(0);
  };
  
  const clearNotifications = () => {
    localStorage.setItem('notifications', JSON.stringify([]));
    setNotifications([]);
    setNotificationCount(0);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setShowDropdown(false);
      
      // Display logout message
      const logoutMessage = document.createElement('div');
      logoutMessage.className = 'fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50';
      logoutMessage.innerHTML = `
        <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl text-center">
          <div class="w-8 h-8 border-4 border-t-blue-500 border-r-blue-500 border-b-gray-200 border-l-gray-200 rounded-full animate-spin mx-auto mb-4"></div>
          <p class="text-gray-800 dark:text-white font-medium">Signing you out...</p>
        </div>
      `;
      document.body.appendChild(logoutMessage);
      
      // Give visual feedback before logout
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Perform logout
      await logout();
      
      // This should never execute because logout redirects the page
      document.body.removeChild(logoutMessage);
    } catch (error) {
      console.error('Error during logout:', error);
      // Force a redirect to login page if logout fails
      window.location.href = '/';
    }
  };

  const handleProfileClick = () => {
    setShowDropdown(false);
    if (onProfileClick) onProfileClick();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <header className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-xl shadow-lg border-b border-slate-200/50 dark:border-gray-700/50 px-8 py-6 ml-72 relative z-50 transition-colors duration-300">
      <div className="flex items-center justify-between">
        {/* Left Section with Logo */}
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <div className="mr-4">
              <div className="flex items-center">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="8" fill="url(#paint0_linear)" />
                  <path d="M11.5 29V11H16.5L20.5 18.5L24.5 11H29.5V29H24.5V18.5L20.5 26L16.5 18.5V29H11.5Z" fill="white" />
                  <defs>
                    <linearGradient id="paint0_linear" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#4F46E5" />
                      <stop offset="1" stopColor="#7C3AED" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="ml-2 text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">FAHIM</span>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {getGreeting()}, <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  {user?.name}
                </span>! ✨
              </h1>
              <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">Welcome to FAHIM - Let's create innovative assessments today</p>
            </motion.div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Search */}
          <motion.button
            onClick={() => setShowSearch(!showSearch)}
            className="relative p-3 text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-300 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiSearch} className="text-lg" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </motion.button>

          {/* Dark Mode Toggle */}
          <motion.button
            onClick={toggleDarkMode}
            className="relative p-3 text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-300 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <SafeIcon icon={darkMode ? FiSun : FiMoon} className="text-lg" />
          </motion.button>

          {/* Notifications */}
          <div className="relative">
            <motion.button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-3 text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-300 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiBell} className="text-lg" />
              {notificationCount > 0 && (
                <>
                  <span className="absolute top-2 right-2 w-2 h-2 bg-gradient-to-r from-red-500 to-pink-500 rounded-full animate-pulse"></span>
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{notificationCount}</span>
                </>
              )}
            </motion.button>
            
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-3 w-80 bg-white dark:bg-gray-800 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-gray-700/50 z-[70] overflow-hidden"
                  style={{ zIndex: 9999 }}
                >
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-800 dark:text-white">Notifications</h3>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Mark all as read
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        onClick={clearNotifications}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {notifications.map(notification => (
                          <div 
                            key={notification.id}
                            className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                              !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                            onClick={() => markAsRead(notification.id)}
                          >
                            <div className="flex items-start">
                              <div className={`p-2 rounded-lg mr-3 ${
                                notification.type === 'urgent' 
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              }`}>
                                <SafeIcon icon={notification.type === 'urgent' ? FiClock : FiCalendar} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-sm text-gray-900 dark:text-white">
                                    {notification.title}
                                  </p>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(notification.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                  {notification.message}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                    notification.type === 'urgent' 
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                  }`}>
                                    {notification.type === 'urgent' ? 'Urgent' : 'Reminder'}
                                  </span>
                                  <button className="text-xs text-primary-600 dark:text-primary-400 flex items-center">
                                    View details
                                    <SafeIcon icon={FiChevronRight} className="ml-1" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                        <SafeIcon icon={FiBell} className="mx-auto text-3xl mb-2 opacity-40" />
                        <p>No notifications</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Menu */}
          <div className="relative z-[60]">
            <motion.button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center space-x-3 p-2 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-300 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="relative">
                <img
                  src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=4F46E5&color=fff`}
                  alt={user?.name || 'User'}
                  className="w-10 h-10 rounded-xl shadow-lg border-2 border-white dark:border-gray-700 group-hover:shadow-xl transition-shadow"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-700"></div>
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300">{user?.email || 'user@example.com'}</p>
              </div>
              <SafeIcon 
                icon={FiChevronDown} 
                className={`text-slate-400 dark:text-slate-300 transition-transform duration-300 ${showDropdown ? 'rotate-180' : ''}`} 
              />
            </motion.button>

            <AnimatePresence>
              {showDropdown && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-3 w-64 bg-white dark:bg-gray-800 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-gray-700/50 z-[70] overflow-hidden"
                  style={{ zIndex: 9999 }}
                >
                  {/* User Info Header */}
                  <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 border-b border-slate-200/50 dark:border-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <img
                        src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=4F46E5&color=fff`}
                        alt={user?.name || 'User'}
                        className="w-12 h-12 rounded-xl shadow-lg"
                      />
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{user?.name || 'User'}</p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">{user?.email || 'user@example.com'}</p>
                        <div className="flex items-center space-x-1 mt-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-xs text-slate-500 dark:text-slate-400">Online</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <motion.button
                      onClick={handleProfileClick}
                      className="flex items-center space-x-3 w-full px-6 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors group"
                      whileHover={{ x: 4 }}
                    >
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-800/30 transition-colors">
                        <SafeIcon icon={FiUser} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="font-semibold">Profile Settings</span>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Manage your account</p>
                      </div>
                    </motion.button>
                  </div>

                  {/* Logout */}
                  <div className="py-2 border-t border-slate-200/50 dark:border-gray-700/50 bg-slate-50/50 dark:bg-gray-800/50">
                    <motion.button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="flex items-center space-x-3 w-full px-6 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors group disabled:opacity-50"
                      whileHover={isLoggingOut ? {} : { x: 4 }}
                    >
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg group-hover:bg-red-200 dark:group-hover:bg-red-800/30 transition-colors">
                        {isLoggingOut ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <SafeIcon icon={FiLogOut} className="text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      <div>
                        <span className="font-semibold">{isLoggingOut ? "Signing Out..." : "Sign Out"}</span>
                        <p className="text-xs text-red-500 dark:text-red-400">
                          {isLoggingOut ? "Please wait..." : "See you later!"}
                        </p>
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6"
          >
            <div className="relative max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search questions, papers, templates..."
                className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-gray-700 border-2 border-slate-200 dark:border-gray-600 rounded-xl focus:bg-white dark:focus:bg-gray-800 focus:border-purple-500 dark:focus:border-purple-400 focus:outline-none transition-all duration-300"
                autoFocus
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;